import React, { useEffect, useRef, useState } from "react";
import { Col, DatePicker, Form, Input, Modal, Row, Select, Tooltip } from "antd";
import { Close, CloudUploadOutlined, Description } from "@mui/icons-material";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { useSelector } from "react-redux";
import { attachmentValidation, getInitials } from "../../../../../utils";

const AddTaskModal = ({ preview, previewHandler, successHandler, status }) => {
  // STATES
  const [isLoading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // CONST VALS
  const { http } = global.services;
  const [form] = Form.useForm();
  const uploadImageInput = useRef(null);

  // REDUX DATA
  const userData = useSelector((state) => state.UserReducer.profile);
  const selectedCompany = useSelector((state) => state.CompanyReducer.selected_company);

  // HANDLERS
  const closeHandler = () => {
    previewHandler();
    form.resetFields();
    setFiles([]);
  };
  const handleFileChange = (e) => {
    const newfiles = e.target.files;
    const validFiles = [];
    for (let i = 0; i < newfiles.length; i++) {
      const file = newfiles[i];
      const isExist = files.find((item) => item.name === file.name);
      const isVaid = attachmentValidation(file);
      if (isExist) toast.error("File already exists");
      if (!isExist && isVaid) {
        validFiles.push(file);
      }
    }
    if (uploadImageInput.current) uploadImageInput.current.value = "";
    if (validFiles.length >= 1) setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFileHandler = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  const submitHandler = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      company_initials: getInitials(selectedCompany.company_name),
      company_id: selectedCompany.id,
      status_id: status,
      timestamp: new Date().getTime(),
      start_date: values.start_date
        ? values.start_date.toISOString().split("T")[0]
        : null,
      end_date: values.end_date ? values.end_date.toISOString().split("T")[0] : null,
    };
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === "assignees" && Array.isArray(value)) {
        value.forEach((assignee) => formData.append("assignees", assignee));
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    // Append files
    files.forEach((file) => formData.append("attachments", file));

    // Send request
    http
      .post(`company-task`, formData)
      .then(({ data }) => {
        toast.success("Task created successfully");
        successHandler(data);
        closeHandler();
      })
      .catch((error) => {
        console.error("Error creating task:", error);
        toast.error("Failed to create task");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (userData?.organization && selectedCompany?.shared) {
      http
        .get(`organization-members`)
        .then(({ data }) => {
          setAssignees(data);
        })
        .catch(() => {});
    } else {
      setAssignees([
        {
          id: userData.id,
          username: userData.username,
        },
      ]);
    }
  }, [userData, selectedCompany]);

  return (
    <Modal
      visible={preview}
      centered
      className="add-task-modal"
      footer={null}
      onCancel={closeHandler}
      closable={false}
      width={800}
    >
      <button className="close-btn" onClick={closeHandler}>
        <Close />
      </button>
      <h2>Create Task</h2>
      <p>Enter task details below</p>
      <Form form={form} onFinish={submitHandler}>
        <Row gutter={16}>
          <Col span={10}>
            <label htmlFor="title">Title</label>
            <Form.Item
              name="title"
              rules={[
                { required: true, message: "Title is required" },
                { max: 100, message: "Title must be less than 100 characters" },
              ]}
            >
              <Input type="text" placeholder="Enter title" />
            </Form.Item>
            <label htmlFor="startDate">Start date</label>
            <Form.Item
              name="start_date"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const endDate = getFieldValue("end_date");
                    if (!value || !endDate || endDate >= value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Start date must be before end date")
                    );
                  },
                }),
              ]}
            >
              <DatePicker id="startDate" />
            </Form.Item>
            <label htmlFor="endDate">End date</label>
            <Form.Item
              name="end_date"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("start_date");
                    if (!value || !startDate || startDate <= value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("End date must be after start date"));
                  },
                }),
              ]}
            >
              <DatePicker id="endDate" />
            </Form.Item>
          </Col>
          <Col span={14}>
            <label htmlFor="description">Description</label>
            <Form.Item name="description">
              <Input.TextArea style={{ height: 130 }} placeholder="Enter description" />
            </Form.Item>
            <label htmlFor="assignees">Assigned To</label>
            <Form.Item name="assignees">
              <Select mode="multiple">
                {assignees.map((assignee, index) => (
                  <Select.Option value={assignee?.id} key={index}>
                    {assignee.username}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <label htmlFor="">Attachments</label>
        <div className="attachments-wrapper">
          <div style={{ display: "flex", gap: 20 }}>
            <input
              multiple
              onChange={handleFileChange}
              ref={uploadImageInput}
              className="upload-attachment-input"
              id="attachmentPicker"
              type="file"
            />
            <label className="upload-box" htmlFor="attachmentPicker">
              <CloudUploadOutlined />
              Upload
            </label>
            {files.map((file, index) => (
              <div key={index} className="file-box">
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => removeFileHandler(index)}
                >
                  <Close />
                </button>
                <div className="preview">
                  {file.type.includes("image") ? (
                    <img src={URL.createObjectURL(file)} alt="" />
                  ) : file.type.includes("pdf") ? (
                    <object data={URL.createObjectURL(file)} type="application/pdf">
                      <embed src={URL.createObjectURL(file)} type="application/pdf" />
                    </object>
                  ) : (
                    <Description />
                  )}
                </div>
                {file.name.length > 20 ? (
                  <Tooltip title={file.name}>
                    <p>{file.name.slice(0, 20)}...</p>
                  </Tooltip>
                ) : (
                  <p>{file.name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="action-box">
          <button type="button" onClick={closeHandler}>
            Cancel
          </button>
          <button className="submit" type="submit">
            {isLoading ? <BeatLoader size={8} color="#fff" /> : "Submit"}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddTaskModal;
